import { createColumnHelper } from '@tanstack/react-table';
import { Fragment, PropsWithChildren } from 'react';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { lt, useFeatureFlag } from '@mews/framework';
import {
    CancellationPolicy,
    PolicyOverrideStatusEnumEnum,
    RateGroupOverview,
    RateGroupRate,
    SettlementRuleResult,
} from '@mews-commander/data-access';
import { Body, ColumnFilterType, Icon, IconName, Link, Stack, Tag } from '@optimus-web/core';
import { Dash } from '@mews-commander/ui';
import { isCurrentEnterprisePaymentAndCancellationPoliciesExceptionsEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { useCommanderSelector } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { useCancellationPolicyNameGetter } from '../../../../components';
import { useRateGroupSidePanelContext } from '../sidePanel/RateGroupSidePanelContext';
import { RateGroupTabValues } from '../../types';
import { useSettlementRuleNameGetter } from '../../../../components/common/SettlementRuleName';
import { PolicyExceptionStatusCell, RowActionCell } from './cells';
import { RequirePaymentDuringOciCell } from './cells/RequirePaymentDuringOciCell';

const columnHelper = createColumnHelper<RateGroupOverview>();

const policyOverrideStatusMapping: Record<PolicyOverrideStatusEnumEnum, 'Empty' | 'Active' | 'Scheduled' | 'Expired'> =
    {
        [PolicyOverrideStatusEnumEnum.Empty]: 'Empty',
        [PolicyOverrideStatusEnumEnum.Active]: 'Active',
        [PolicyOverrideStatusEnumEnum.Scheduled]: 'Scheduled',
        [PolicyOverrideStatusEnumEnum.Expired]: 'Expired',
    };

const policyOverrideStatusMultiselectOptions = [
    { value: 'Active', label: lt('RateGroupPolicyExceptionStatusActive') },
    { value: 'Expired', label: lt('RateGroupPolicyExceptionStatusExpired') },
    { value: 'Scheduled', label: lt('RateGroupPolicyExceptionStatusScheduled') },
];

const LinkCell = ({
    children,
    tab,
    rateGroup,
}: PropsWithChildren<{ rateGroup: RateGroupOverview; tab: RateGroupTabValues }>) => {
    const { open } = useRateGroupSidePanelContext();
    return (
        <Link
            standalone
            variant="basic"
            onClick={() => {
                open({ rateGroup, tab });
            }}
            data-disable-click-away
        >
            {children}
        </Link>
    );
};

const RateNames = ({ activeRateCount, activeRates }: { activeRateCount: number; activeRates: RateGroupRate[] }) => (
    <Stack distribution="space-between" spacing="100">
        <Body size="small" ellipsis maxLines={1}>
            {activeRates.map((rate, index) => (
                <Fragment key={rate.id}>
                    {rate.name}
                    {index < activeRates.length - 1 && `${UnicodeEntity.Comma}${UnicodeEntity.Space}`}
                </Fragment>
            ))}
        </Body>
        <Tag variant="basicSubtle">{activeRateCount}</Tag>
    </Stack>
);

const CancellationPolicyNames = ({ cancellationPolicies }: { cancellationPolicies: Array<CancellationPolicy> }) => {
    const getCancellationPolicyName = useCancellationPolicyNameGetter();
    return (
        <>
            {cancellationPolicies.map((cancellationPolicy, index) => (
                <Fragment key={cancellationPolicy.id}>
                    {getCancellationPolicyName(cancellationPolicy, {
                        format: 'nameAndParameters',
                        nonBreakingSpaces: false,
                    })}
                    {index < cancellationPolicies.length - 1 && `${UnicodeEntity.Comma}${UnicodeEntity.Space}`}
                </Fragment>
            ))}
        </>
    );
};

const SettlementRuleNames = ({ settlementRules }: { settlementRules: Array<SettlementRuleResult> }) => {
    const getSettlementRuleName = useSettlementRuleNameGetter();
    return (
        <>
            {settlementRules.map((settlementRule, index) => (
                <Fragment key={settlementRule.id}>
                    {getSettlementRuleName(settlementRule, {
                        format: 'nameAndParameters',
                        nonBreakingSpaces: false,
                    })}
                    {index < settlementRules.length - 1 && `${UnicodeEntity.Comma}${UnicodeEntity.Space}`}
                </Fragment>
            ))}
        </>
    );
};

export const useRateGroupTableColumns = () => {
    const arePolicyExceptionsEnabled = useCommanderSelector(
        isCurrentEnterprisePaymentAndCancellationPoliciesExceptionsEnabled
    );
    const requirePaymentDuringOCI = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    return [
        columnHelper.accessor('name', {
            header: () => lt(TextKey.Name),
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
                width: 2001,
            },
            cell: (props) => (
                <LinkCell rateGroup={props.row.original} tab="general">
                    <Stack spacing="100" itemAlignment="center">
                        {props.row.original.isPortfolioManaged ? (
                            <Icon size="small" value={IconName.CustomLink} />
                        ) : null}
                        <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                            {props.row.original.name}
                        </Body>
                    </Stack>
                </LinkCell>
            ),
        }),
        columnHelper.accessor('activeRates', {
            header: () => lt(TextKey.RatePlural),
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
                width: 2001,
            },
            cell: (props) => (
                <LinkCell rateGroup={props.row.original} tab="rates">
                    {props.row.original.activeRateCount > 0 ? <RateNames {...props.row.original} /> : <Dash />}
                </LinkCell>
            ),
        }),
        columnHelper.accessor('settlementRules', {
            header: () => lt(TextKey.PaymentPolicyPlural),
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
                width: 2001,
            },
            cell: (props) => (
                <LinkCell rateGroup={props.row.original} tab="policies">
                    <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                        <SettlementRuleNames settlementRules={props.getValue()} />
                    </Body>
                </LinkCell>
            ),
        }),
        columnHelper.accessor('cancellationPolicies', {
            header: () => lt(TextKey.CancellationPolicyPlural),
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
                width: 2001,
            },
            cell: (props) => {
                const cancellationPolicies = props.getValue();
                return (
                    <LinkCell rateGroup={props.row.original} tab="policies">
                        {cancellationPolicies && cancellationPolicies.length > 0 ? (
                            <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                                <CancellationPolicyNames cancellationPolicies={cancellationPolicies} />
                            </Body>
                        ) : (
                            <Dash />
                        )}
                    </LinkCell>
                );
            },
        }),
        arePolicyExceptionsEnabled
            ? columnHelper.accessor('policyOverrideStatus', {
                  header: () => lt(TextKey.GeneralPolicyExceptionPlural),
                  enableColumnFilter: true,
                  meta: {
                      filtersType: ColumnFilterType.MultiSelect,
                      getMultiSelectOptions: () => policyOverrideStatusMultiselectOptions,
                  },
                  cell: (props) => {
                      const value = props.getValue();
                      if (!value) return <Dash />;
                      const status = policyOverrideStatusMapping[value];
                      return status !== 'Empty' ? <PolicyExceptionStatusCell status={status} /> : <Dash />;
                  },
              })
            : null,
        requirePaymentDuringOCI
            ? columnHelper.accessor((row) => (row.requirePaymentCardDuringOnlineCheckIn ? 'Required' : 'Hidden'), {
                  id: 'requirePaymentCardDuringOnlineCheckIn',
                  header: () => lt(TextKey.CheckInRequirePaymentDetailsHeaderCell),
                  enableColumnFilter: true,
                  meta: {
                      filtersType: ColumnFilterType.MultiSelect,
                      getMultiSelectOptions: () => [
                          { value: 'Required', label: lt('Required') },
                          { value: 'Hidden', label: lt('Hidden') },
                      ],
                  },
                  cell: (props) => <RequirePaymentDuringOciCell status={props.getValue()} />,
              })
            : null,
        columnHelper.accessor('ordering', {
            header: () => lt('Ordering'),
            enableSorting: true,
        }),
        columnHelper.display({
            id: 'actions',
            meta: { align: 'end' },
            cell: (props) =>
                props.row.original.isPortfolioManaged ? null : <RowActionCell rateGroupId={props.row.id} />,
        }),
    ].filter((item) => item !== null);
};
